// University of Mississippi: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Ole Miss's Common Data Set 2025-2026 (UM_CDS_2025_2026.pdf), the file
//    Institutional Research, Effectiveness and Planning links from its Common
//    Data Set page (the link opens a Box share at olemiss.box.com; the
//    SOURCES url is the olemiss.edu page that links it). The PDF is a filled
//    form; the C8A and C21/C22 answers were read from its form fields. C1
//    applicants, admits and enrollees for Fall 2025 with the residency table;
//    C8A (SAT or ACT "required for some"); C21/C22 (no early decision, no
//    early action). Sections G1 and G5 are blank in this file.
//  - Ole Miss Undergraduate Admissions: Freshman Admission Requirements (the
//    undergraduate admissions page), Application Process, and Competitive
//    Academic Programs. Admission is rolling; the pages give no fixed
//    deadline and no decision dates, only a February 1 priority date for
//    nonresidents and a January 10 date for the scholarship and competitive
//    programs applications. The pages carry no cycle year; the Competitive
//    Academic Programs page names the Fall 2027 cycle.
//  - Ole Miss Admissions, Cost of Attendance, "Undergraduate Full-Time
//    Student (Campus Housing)". Quirk: the page states no academic year. Its
//    tuition rate ($428.25 per semester hour for residents) is the rate the
//    university's Tuition Calculator uses for its "Fall / Spring 2026/27"
//    term, so the figures are taken as 2026-2027. The page's totals equal
//    the sum of its items.
//  - College Scorecard, school id 176017 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Ole Miss Common Data Set 2025-2026',
        url: 'https://olemiss.edu/irep/institutional-research/common-data/',
        read: '2026-10-01',
    },
    requirements: {
        label: 'Ole Miss Undergraduate Admissions, Freshman Admission Requirements',
        url: 'https://olemiss.edu/admissions/undergraduate-admissions/index.php',
        read: '2026-10-01',
    },
    process: {
        label: 'Ole Miss Undergraduate Admissions, Application Process',
        url: 'https://olemiss.edu/admissions/undergraduate-admissions/application-process/index.php',
        read: '2026-10-01',
    },
    competitive: {
        label: 'Ole Miss Academics, Competitive Academic Programs',
        url: 'https://olemiss.edu/academics/competitive-programs/index.php',
        read: '2026-10-01',
    },
    costs: {
        label: 'Ole Miss Admissions, Cost of Attendance',
        url: 'https://olemiss.edu/admissions/cost-of-attendance/index.php',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?176017-University-of-Mississippi',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-mississippi',
    kbId: 'university_of_mississippi',
    name: 'University of Mississippi',
    shortName: 'Ole Miss',
    aliases: ['Ole Miss'],
    location: 'Oxford, MS',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Ole Miss licensing colors); decoration only.
    branding: {
        primary: '#C8102E',
        secondary: '#13294B',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_mississippi_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 37621,
        admitted: 36635,
        enrolled: 5458,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 4307, admitted: 4230 },
            { group: 'Out-of-state', applied: 32875, admitted: 31976 },
            { group: 'International', applied: 439, admitted: 429 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Ole Miss\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'Ole Miss has no early decision or early action plan. Admission is rolling, and February 1 is a priority date, not an early round.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Ole Miss, not to a school. Its competitive academic programs take a separate application:',
        rows: [
            { school: 'Competitive academic programs', requirement: 'The Sally McDonnell Barksdale Honors College, the Center for Manufacturing Excellence, the Croft Institute for International Studies, the Early Entry Pharmacy Program, the Lott Leadership Institute, the Mississippi Excellence in Teaching Program and Southern Studies take one shared application (CAPApp), due January 10.', source: 'competitive' },
        ],
        note: 'The separate application for the leadership, service and support programs (Bonner Leaders, FASTrack) for Fall 2027 opens November 1.',
        source: 'competitive',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Admission', value: 'Rolling' },
        rows: [
            { plan: 'Freshman Scholarship and competitive programs applications', deadline: 'January 10', decision: null, binding: false },
            { plan: 'Priority date (nonresidents)', deadline: 'February 1', decision: null, binding: false },
            { plan: 'Rolling admission', deadline: 'Until the class is full', decision: null, binding: false },
        ],
        note: 'Nonresidents are admitted on a competitive, rolling basis until the university reaches capacity, and Ole Miss suggests they apply with all documents by February 1 of senior year. It asks you to allow three to four weeks for an application to be processed and does not publish decision dates.',
        source: 'requirements',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Scores are optional for some applicants and needed by others. Mississippi residents with at least a 3.2 unweighted GPA and the College Preparatory Curriculum are admitted without a score; residents below 3.2 need a minimum ACT composite (16 with a 2.5 GPA or top-half class rank, or 18 with a 2.0 GPA) or the SAT equivalent. For nonresidents, the ACT or SAT is optional with a 3.2 unweighted GPA or higher and strongly recommended below it. Ole Miss superscores within the ACT or within the SAT, not across them.',
        source: 'requirements',
        cdsLine: 'The Common Data Set marks the SAT or ACT as required for some applicants for fall 2027, and notes that "Test scores are recommended, and submitted test scores are reviewed for admissions."',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'process',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [10278, 31068] },
            { item: 'Capital Improvements Fee', amounts: [100, 100] },
            { item: 'Student Activity Fee', amounts: [60, 60] },
            { item: 'Housing (on campus, average)', amounts: [8030, 8030] },
            { item: 'Food', amounts: [5580, 5580] },
            { item: 'Books and supplies', amounts: [1200, 1200] },
            { item: 'Personal expenses', amounts: [3500, 3500] },
            { item: 'Transportation', amounts: [3400, 3400] },
        ],
        totals: [32148, 52938],
        note: 'For a full-time undergraduate in campus housing, fall and spring. Housing is the average for university-managed rooms; Ole Miss gives a range of $6,134 to $16,000 depending on the room and building.',
        source: 'costs',
        netPrice: {
            average: 13752,
            byIncome: [
                { income: '$0 to $30,000', amount: 10536 },
                { income: '$30,001 to $48,000', amount: 10185 },
                { income: '$48,001 to $75,000', amount: 13815 },
                { income: '$75,001 to $110,000', amount: 17443 },
                { income: 'Over $110,000', amount: 22290 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Ole Miss\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
